import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  CreateHomeworkAssignmentRequestBody,
  GetTopicsCourseType,
} from "@packages/api/madlenAPI.schemas";
import { CourseData, isTurkishCourse } from "@packages/constant/CourseData";
import CommonPropertiesStep from "./CommonPropertiesStep";
import { useToast } from "@packages/contexts/ToastContext";
import CourseSelector from "../course-selector";
import { UnitTopicSelector } from "@packages/components/unit-topic-selector";
import GradeSelector from "../../../../tools/components/grade-selector";
import { Text } from "@core/text";
import { useAppSelector } from "@packages/store/hooks";
import { Button } from "@core/button";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector";

export type CurriculumType = "MEB" | "INTERNATIONAL";
export type InternationalCurriculum = "AP" | "IGCSE" | "IB" | "ALEVEL";
interface TopicStepProps {
  assignmentData: CreateHomeworkAssignmentRequestBody;
  onChange: (data: Partial<CreateHomeworkAssignmentRequestBody>) => void;
}

const TopicStep = ({ assignmentData, onChange }: TopicStepProps) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const [selectedGrade, setSelectedGrade] = useState<number>(
    assignmentData.grade ?? 8,
  );

  // Initialize curriculum type based on language
  const [curriculumType, setCurriculumType] = useState<CurriculumType>(
    i18n.language === "tr" ? "MEB" : "INTERNATIONAL",
  );
  const [internationalCurriculum, setInternationalCurriculum] =
    useState<InternationalCurriculum>("AP");

  // Initialize selected course based on language and curriculum type
  const getInitialCourse = () => {
    if (i18n.language === "tr") {
      return "HISTORY";
    } else {
      return "AP_2025_WORLD_HISTORY_MODERN";
    }
  };

  const [selectedCourse, setSelectedCourse] = useState<GetTopicsCourseType>(
    assignmentData.courseType ?? getInitialCourse(),
  );

  const [selectedTopics, setSelectedTopics] = useState<number[]>(
    assignmentData.topicIds ?? [],
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    assignmentData.contentLanguage || i18n.language,
  );

  useEffect(() => {
    console.log("Generator changed to TOPIC_LIST");
    onChange({ generator: "TOPIC_LIST" });
  }, []);

  const userEmail = useAppSelector((state) => state.user.teacher?.email);
  const shouldShowMathematics =
    userEmail === "batuhan@madlen.io" || userEmail === "kadriyezobu@gmail.com";

  // Update curriculum type when language changes
  useEffect(() => {
    const newCurriculumType = i18n.language === "tr" ? "MEB" : "INTERNATIONAL";
    setCurriculumType(newCurriculumType);

    // Set appropriate course based on new curriculum type
    const newCourse = getInitialCourse();
    setSelectedCourse(newCourse);
    setSelectedTopics([]);
    onChange({
      courseType: newCourse,
      topicIds: [],
    });
  }, [i18n.language]);

  const getFilteredCourses = (): GetTopicsCourseType[] => {
    if (curriculumType === "MEB") {
      return Object.keys(CourseData).filter(
        (key) =>
          isTurkishCourse(key) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    } else {
      return Object.keys(CourseData).filter(
        (key) =>
          key.startsWith(`${internationalCurriculum}_`) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    }
  };

  // Update selected course when curriculum type or international curriculum changes
  useEffect(() => {
    const filteredCourses = getFilteredCourses();

    // Check if current course is still valid
    if (!filteredCourses.includes(selectedCourse)) {
      // Select first available course for the current curriculum
      if (filteredCourses.length > 0) {
        const newCourse = filteredCourses[0];
        setSelectedCourse(newCourse);
        setSelectedTopics([]);
        onChange({
          courseType: newCourse,
          topicIds: [],
        });
      }
    }
  }, [curriculumType, internationalCurriculum]);

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg mb-6">
        <Text variant="p" status="info" size="sm">
          assignments.generator.topic.title
        </Text>
        <Text variant="p" status="info" size="sm">
          assignments.generator.topic.description
        </Text>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CommonPropertiesStep
          assignmentData={assignmentData}
          onChange={onChange}
        />
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            <GradeSelector
              selectedGrade={selectedGrade}
              minGrade={1}
              setSelectedGrade={(grade) => {
                setSelectedGrade(grade);

                // Get available courses for new grade
                const availableCourses = getFilteredCourses().filter((course) =>
                  CourseData[course].visible(grade),
                );

                // Check if current course is still available
                const isCurrentCourseAvailable =
                  availableCourses.includes(selectedCourse);

                if (!isCurrentCourseAvailable && availableCourses.length > 0) {
                  const newCourse = availableCourses[0];
                  setSelectedCourse(newCourse);
                  onChange({
                    grade,
                    courseType: newCourse,
                    topicIds: [],
                  });
                } else {
                  onChange({
                    grade,
                    topicIds: [],
                  });
                }

                setSelectedTopics([]);
              }}
            />
          </div>
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("hooks.form.outputLanguage")}
          </h4>
          <OutputLanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={(language) => {
              setSelectedLanguage(language);
              onChange({ contentLanguage: language });
            }}
          />
        </div>
      </div>
      <div className="space-y-4">
        {/* Curriculum Type Selection */}
        <div className="space-y-2">
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("common.curriculumType")}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              onClick={() => {
                setCurriculumType("MEB");
                // Reset to first available MEB course
                const mebCourses = Object.keys(CourseData).filter(
                  (key) =>
                    isTurkishCourse(key) &&
                    CourseData[key as GetTopicsCourseType].visible(
                      selectedGrade,
                    ),
                ) as GetTopicsCourseType[];

                if (mebCourses.length > 0) {
                  setSelectedCourse(mebCourses[0]);
                  setSelectedTopics([]);
                  onChange({
                    courseType: mebCourses[0],
                    topicIds: [],
                  });
                }
              }}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "MEB"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "MEB"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.nationalCurriculum")}
              </span>
            </Button>
            <Button
              type="button"
              onClick={() => {
                setCurriculumType("INTERNATIONAL");
                // Reset to first available international course
                const intlCourses = Object.keys(CourseData).filter(
                  (key) =>
                    key.startsWith(`${internationalCurriculum}_`) &&
                    CourseData[key as GetTopicsCourseType].visible(
                      selectedGrade,
                    ),
                ) as GetTopicsCourseType[];

                if (intlCourses.length > 0) {
                  setSelectedCourse(intlCourses[0]);
                  setSelectedTopics([]);
                  onChange({
                    courseType: intlCourses[0],
                    topicIds: [],
                  });
                }
              }}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "INTERNATIONAL"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "INTERNATIONAL"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.internationalCurriculum")}
              </span>
            </Button>
          </div>
        </div>

        {/* International Curriculum Sub-selection */}
        {curriculumType === "INTERNATIONAL" && (
          <div className="space-y-2">
            <h4
              className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
            >
              {t("common.selectInternationalProgram")}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(
                ["AP", "IGCSE", "IB", "ALEVEL"] as InternationalCurriculum[]
              ).map((curriculum) => (
                <Button
                  key={curriculum}
                  type="button"
                  onClick={() => {
                    setInternationalCurriculum(curriculum);
                    // Update course selection based on new international curriculum
                    const intlCourses = Object.keys(CourseData).filter(
                      (key) =>
                        key.startsWith(`${curriculum}_`) &&
                        CourseData[key as GetTopicsCourseType].visible(
                          selectedGrade,
                        ),
                    ) as GetTopicsCourseType[];

                    if (intlCourses.length > 0) {
                      setSelectedCourse(intlCourses[0]);
                      setSelectedTopics([]);
                      onChange({
                        courseType: intlCourses[0],
                        topicIds: [],
                      });
                    }
                  }}
                  variant="solid"
                  size="md"
                  rounded="lg"
                  textColor={
                    internationalCurriculum === curriculum
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-400"
                  }
                  bgColor={
                    internationalCurriculum === curriculum
                      ? `bg-indigo-600 hover:bg-indigo-700`
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }
                  fullWidth
                >
                  <span className="text-sm font-medium">{curriculum}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1">
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CourseSelector
              selectedGrade={selectedGrade}
              selectedCourse={selectedCourse}
              filteredCourses={Object.values(GetTopicsCourseType)
                .filter((course) => !getFilteredCourses().includes(course))
                .concat(
                  shouldShowMathematics
                    ? []
                    : (["MATHEMATICS"] as GetTopicsCourseType[]),
                )}
              setSelectedCourse={(course) => {
                setSelectedCourse(course);
                setSelectedTopics([]);
                onChange({
                  grade: selectedGrade,
                  courseType: course,
                  topicIds: [],
                });
              }}
            />
          </div>
        </div>
      </div>

      {selectedGrade && selectedCourse && (
        <UnitTopicSelector
          grade={selectedGrade}
          courseType={selectedCourse}
          selectedTopics={selectedTopics}
          setSelectedTopics={(topicIds) => {
            if (topicIds.length > 5) {
              addToast({
                title: t("notifications.assignment.topicLimit"),
                variant: "warning",
                duration: 3000,
              });
              return;
            }
            setSelectedTopics(topicIds);

            onChange({
              grade: selectedGrade,
              courseType: selectedCourse,
              topicIds,
            });
          }}
        />
      )}
    </div>
  );
};

export default TopicStep;
